import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchLeads, fetchStats, updateLeadStatus, deleteLead } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Mail, Phone, Search, ExternalLink } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "closed"];
const STATUS_LABEL = { new: "Neu", contacted: "Kontaktiert", qualified: "Qualifiziert", closed: "Abgeschlossen" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("aleger_admin_token") : null;
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(token), fetchStats(token)]);
      setLeads(l);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("aleger_admin_token");
        navigate("/admin");
      } else {
        toast.error("Leads konnten nicht geladen werden");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    load();
  }, [token, load, navigate]);

  const logout = () => {
    localStorage.removeItem("aleger_admin_token");
    navigate("/admin");
  };

  const onStatus = async (id, status) => {
    try {
      await updateLeadStatus(token, id, status);
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status aktualisiert");
    } catch {
      toast.error("Aktualisierung fehlgeschlagen");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Diesen Lead wirklich löschen?")) return;
    try {
      await deleteLead(token, id);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Gelöscht");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const filtered = leads.filter((l) => {
    if (filter !== "all" && l.status !== filter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return [l.name, l.email, l.company, l.message].some((f) => (f || "").toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="admin-dashboard">
      {/* topbar */}
      <header className="bg-[#0B1121] text-white border-b border-blue-600/40">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-blue-600 flex items-center justify-center font-display font-black">A</div>
            <div>
            <div className="font-display font-black tracking-tight">ALEGER · Leads</div>
              <div className="text-[10px] uppercase tracking-widest font-mono-tech text-slate-400">Admin-Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid="link-site">
              <ExternalLink size={14} /> Zur Website
            </Link>
            <button onClick={load} className="text-sm border border-white/15 px-3 py-1.5 hover:border-blue-500 inline-flex items-center gap-2" data-testid="refresh-leads">
              <RefreshCw size={14} /> Aktualisieren
            </button>
            <button onClick={logout} className="text-sm bg-blue-600 px-3 py-1.5 hover:bg-blue-700 inline-flex items-center gap-2" data-testid="admin-logout">
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      {/* stats */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        {[
          { label: "Leads gesamt", value: stats.total },
          { label: "Neu", value: stats.new },
          { label: "Kontaktiert", value: stats.contacted },
          { label: "Abgeschlossen", value: stats.closed },
        ].map((s) => (
          <div key={s.label} className="bg-white p-6" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
            <div className="text-xs uppercase tracking-widest font-mono-tech text-slate-500">{s.label}</div>
            <div className="font-display font-black text-3xl mt-2 text-slate-900">{s.value}</div>
          </div>
        ))}
      </div>

      {/* filters */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 pb-4 flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`text-xs uppercase tracking-widest font-mono-tech px-3 py-1.5 border ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
            >
              {s === "all" ? "Alle" : STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="search-leads"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche Name, E-Mail, Unternehmen…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
          />
        </div>
      </div>

      {/* table + detail */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 pb-16 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-widest font-mono-tech text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Unternehmen</th>
                <th className="text-left px-4 py-3">E-Mail</th>
                <th className="text-left px-4 py-3">Datum</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-12 text-slate-500">Wird geladen…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-slate-500" data-testid="empty-leads">Noch keine Leads vorhanden.</td></tr>
              ) : filtered.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => setSelected(l)}
                  className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${selected?.id === l.id ? "bg-blue-50/50" : ""}`}
                  data-testid={`lead-row-${l.id}`}
                >
                  <td className="px-4 py-3 font-medium">{l.name}</td>
                  <td className="px-4 py-3 text-slate-600">{l.company}</td>
                  <td className="px-4 py-3 text-slate-600">{l.email}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono-tech text-xs">
                    {new Date(l.created_at).toLocaleDateString("de-DE")}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={l.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => onStatus(l.id, e.target.value)}
                      className="text-xs uppercase tracking-widest font-mono-tech border border-slate-300 px-2 py-1 bg-white"
                      data-testid={`status-select-${l.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(l.id); }}
                      className="text-slate-400 hover:text-red-600"
                      data-testid={`delete-${l.id}`}
                      aria-label="delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="bg-white border border-slate-200 p-6" data-testid="lead-detail-panel">
          {selected ? (
            <div>
              <div className="text-xs uppercase tracking-widest font-mono-tech text-blue-600 mb-2">Lead-Details</div>
              <div className="font-display font-black text-2xl">{selected.name}</div>
              <div className="text-slate-500 text-sm mt-1">{selected.company}</div>
              <div className="mt-6 space-y-2 text-sm">
                <div className="flex items-center gap-2"><Mail size={14} className="text-blue-600" /> <a href={`mailto:${selected.email}`} className="hover:underline">{selected.email}</a></div>
                <div className="flex items-center gap-2"><Phone size={14} className="text-blue-600" /> {selected.phone}</div>
              </div>
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest font-mono-tech text-slate-500 mb-2">Nachricht</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>
              <div className="mt-6 text-xs font-mono-tech text-slate-500">
                Quelle: {selected.source || "-"} · {new Date(selected.created_at).toLocaleString("de-DE")}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500 text-center py-10">Wählen Sie einen Lead aus, um Details anzuzeigen.</div>
          )}
        </aside>
      </div>
    </div>
  );
}
